import { OrganizationUserAccessTypes } from '@generated/graphql';
import type { RepCode } from '@interfaces/repCode';
import { RepCodeSelect } from '@ovComponents/3-pattern/repCodeSelect/repCodeSelect';
import { UserContext } from '@providers/userContextProvider';
import { Alert } from 'hephaestus/web';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../../../../hooks/usePermission';

const REP_CODE_TEST_ID = 'create-account-for-goal-rep-code';

export const useRepCodeSelection = () => {
  const { t } = useTranslation('workflowCompletions');
  const { userContext } = useContext(UserContext);
  const canReadRepCode = usePermission('read:rep_code');
  const [pickedRepCode, setPickedRepCode] = useState<RepCode | undefined>();
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const isRepCodeUser =
    userContext.accessType === OrganizationUserAccessTypes.RepCode;
  const requiresRepCode = isRepCodeUser && canReadRepCode;
  const hasNoRepCodeAccess = isRepCodeUser && !canReadRepCode;
  const assignedRepCodes = userContext.repCodes ?? [];
  const soleRepCode =
    requiresRepCode && assignedRepCodes.length === 1
      ? assignedRepCodes[0]
      : undefined;

  // The single assigned code is the default value, not mirrored state: deriving
  // it keeps the first render correct and picks up a late userContext load.
  const repCode = pickedRepCode ?? soleRepCode;
  const repCodeMissing = isRepCodeUser && !repCode?.code;
  const showRepCodeError = submitAttempted && repCodeMissing;

  const validateRepCode = () => {
    setSubmitAttempted(true);
    return !repCodeMissing;
  };

  return {
    repCode,
    isRepCodeUser,
    repCodeMissing,
    validateRepCode,
    repCodeField: (
      <>
        {hasNoRepCodeAccess && (
          <Alert
            severity='warning'
            sx={{ mb: 3 }}
            data-testid='rep-code-no-access'
          >
            {t('createAccount.repCodeNoAccess')}
          </Alert>
        )}
        {requiresRepCode && (
          <RepCodeSelect
            mode='assigned'
            repCode={repCode}
            setRepCode={setPickedRepCode}
            label={t('createAccount.repCodeLabel')}
            testId={REP_CODE_TEST_ID}
            fullWidth
            sx={{ mb: 3 }}
            {...(soleRepCode
              ? {
                  locked: true,
                  lockMessage: t('createAccount.repCodeOnlyOne'),
                }
              : {
                  error: showRepCodeError,
                  errorText: t('createAccount.repCodeRequired'),
                  placeholder: t('createAccount.repCodePlaceholder'),
                  helpText: t('createAccount.repCodeHelpText'),
                  noOptionsText: t('createAccount.repCodeNoneAssigned'),
                })}
          />
        )}
      </>
    ),
  };
};
